import {
  CURRENT_YEAR,
  DEFAULT_PROFILE_ID,
  emitProfileDataChanged,
  ensureSeedData,
  openDatabase,
  readAllFromIndex,
  readAllFromStore,
  requestToPromise,
  STORE_APP_META,
  STORE_BUDGET_CATEGORIES,
  STORE_EXPENSES,
  STORE_PROFILES,
  STORE_PROFILE_SETTINGS,
  STORE_YEARS,
  transactionDone
} from "./walletWatchDbCore.js";
import {
  mergeProfileSettings,
  normalizeProfileSettings
} from "../settings/profileSettings.js";

export function normalizeProfileRecord(profile) {
  return {
    id: profile.id,
    name: profile.name ?? "",
    source: profile.source ?? "local",
    isDefault: Boolean(profile.isDefault),
    archivedAt: profile.archivedAt ?? null,
    createdAt: profile.createdAt ?? null,
    updatedAt: profile.updatedAt ?? null
  };
}

function normalizeYearRecord(yearRecord) {
  return {
    id: yearRecord.id,
    profileId: yearRecord.profileId,
    year: yearRecord.year,
    archivedAt: yearRecord.archivedAt ?? null,
    createdAt: yearRecord.createdAt ?? null,
    updatedAt: yearRecord.updatedAt ?? null
  };
}

function sortYearsDescending(years) {
  return years
    .slice()
    .sort((left, right) => String(right.year ?? "").localeCompare(String(left.year ?? "")));
}

function findMostRecentAvailableYear(years) {
  return sortYearsDescending(years).find((yearRecord) => !yearRecord.archivedAt) ?? null;
}

function getFreshFallbackYear(existingYears) {
  const usedYears = new Set(
    existingYears
      .map((entry) => String(entry?.year ?? "").trim())
      .filter(Boolean)
  );
  let nextYearNumber = Number(CURRENT_YEAR);

  if (!Number.isFinite(nextYearNumber)) {
    return CURRENT_YEAR;
  }

  while (usedYears.has(String(nextYearNumber))) {
    nextYearNumber += 1;
  }

  return String(nextYearNumber);
}

export async function ensureProfileYearRecord(yearsStore, profileId, year, now) {
  const existingYear = await requestToPromise(yearsStore.index("profileYear").get([profileId, year]));
  if (existingYear) {
    return existingYear;
  }

  const yearRecord = {
    id: `${profileId}:${year}`,
    profileId,
    year,
    archivedAt: null,
    createdAt: now,
    updatedAt: now
  };
  yearsStore.put(yearRecord);
  return yearRecord;
}

async function ensureAvailableYearScope(yearsStore, profileId, now) {
  const profileYears = await readAllFromIndex(yearsStore, "profileId", profileId);
  const fallbackYear = findMostRecentAvailableYear(profileYears);

  if (fallbackYear) {
    return fallbackYear;
  }

  const year = getFreshFallbackYear(profileYears);
  return ensureProfileYearRecord(yearsStore, profileId, year, now);
}

export async function listProfiles() {
  await ensureSeedData();
  const db = await openDatabase();
  const transaction = db.transaction(STORE_PROFILES, "readonly");
  const profiles = await readAllFromStore(transaction.objectStore(STORE_PROFILES));
  await transactionDone(transaction);

  return profiles
    .filter((profile) => !profile.archivedAt)
    .map(normalizeProfileRecord)
    .sort((left, right) => {
      if (left.isDefault !== right.isDefault) {
        return left.isDefault ? -1 : 1;
      }

      return left.name.localeCompare(right.name);
    });
}

export async function listProfileYears(profileId, options = {}) {
  await ensureSeedData();
  const db = await openDatabase();
  const transaction = db.transaction(STORE_YEARS, "readonly");
  const years = await readAllFromIndex(transaction.objectStore(STORE_YEARS), "profileId", profileId);
  await transactionDone(transaction);

  const includeArchived = options.includeArchived ?? false;

  return sortYearsDescending(
    years
      .map(normalizeYearRecord)
      .filter((yearRecord) => includeArchived || !yearRecord.archivedAt)
  );
}

export async function setActiveBudgetScope(profileId, year) {
  await ensureSeedData();
  const db = await openDatabase();
  const transaction = db.transaction([STORE_PROFILES, STORE_YEARS, STORE_APP_META], "readwrite");
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const profile = await requestToPromise(profilesStore.get(profileId));

  if (!profile || profile.archivedAt) {
    transaction.abort();
    throw new Error(`Profile "${profileId}" could not be activated.`);
  }

  const yearRecord = await requestToPromise(yearsStore.index("profileYear").get([profileId, year]));
  if (!yearRecord || yearRecord.archivedAt) {
    transaction.abort();
    throw new Error(`Year "${year}" is not available for profile "${profileId}".`);
  }

  const appMetaStore = transaction.objectStore(STORE_APP_META);
  appMetaStore.put({ key: "activeProfileId", value: profileId });
  appMetaStore.put({ key: "activeYear", value: year });
  await transactionDone(transaction);

  return {
    profileId,
    year
  };
}

export async function archiveProfileYear(profileId, year) {
  await ensureSeedData();
  const db = await openDatabase();
  const transaction = db.transaction([STORE_PROFILES, STORE_YEARS, STORE_APP_META], "readwrite");
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const appMetaStore = transaction.objectStore(STORE_APP_META);
  const [profile, targetYearRecord, activeProfileIdRecord, activeYearRecord] = await Promise.all([
    requestToPromise(profilesStore.get(profileId)),
    requestToPromise(yearsStore.index("profileYear").get([profileId, year])),
    requestToPromise(appMetaStore.get("activeProfileId")),
    requestToPromise(appMetaStore.get("activeYear"))
  ]);

  if (!profile || profile.archivedAt) {
    transaction.abort();
    throw new Error(`Profile "${profileId}" could not be found.`);
  }

  if (!targetYearRecord || targetYearRecord.archivedAt) {
    transaction.abort();
    throw new Error(`Year "${year}" could not be archived.`);
  }

  const now = new Date().toISOString();
  yearsStore.put({
    ...targetYearRecord,
    archivedAt: now,
    updatedAt: now
  });

  let nextScope = null;
  const activeProfileId = activeProfileIdRecord?.value ?? DEFAULT_PROFILE_ID;
  const activeYear = activeYearRecord?.value ?? CURRENT_YEAR;

  if (activeProfileId === profileId && activeYear === year) {
    const fallbackYearRecord = await ensureAvailableYearScope(yearsStore, profileId, now);
    appMetaStore.put({ key: "activeYear", value: fallbackYearRecord.year });
    nextScope = {
      profileId,
      year: fallbackYearRecord.year
    };
  }

  await transactionDone(transaction);
  emitProfileDataChanged({
    profileId,
    reason: "archive-year",
    changeType: "structure"
  });

  return {
    archivedYear: normalizeYearRecord({
      ...targetYearRecord,
      archivedAt: now,
      updatedAt: now
    }),
    nextScope
  };
}

export async function unarchiveProfileYear(profileId, year) {
  await ensureSeedData();
  const db = await openDatabase();
  const transaction = db.transaction([STORE_PROFILES, STORE_YEARS], "readwrite");
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const [profile, targetYearRecord] = await Promise.all([
    requestToPromise(profilesStore.get(profileId)),
    requestToPromise(yearsStore.index("profileYear").get([profileId, year]))
  ]);

  if (!profile || profile.archivedAt) {
    transaction.abort();
    throw new Error(`Profile "${profileId}" could not be found.`);
  }

  if (!targetYearRecord || !targetYearRecord.archivedAt) {
    transaction.abort();
    throw new Error(`Year "${year}" could not be unarchived.`);
  }

  const now = new Date().toISOString();
  yearsStore.put({
    ...targetYearRecord,
    archivedAt: null,
    updatedAt: now
  });
  await transactionDone(transaction);
  emitProfileDataChanged({
    profileId,
    reason: "unarchive-year",
    changeType: "structure"
  });

  return normalizeYearRecord({
    ...targetYearRecord,
    archivedAt: null,
    updatedAt: now
  });
}

export async function deleteProfileYear(profileId, year) {
  await ensureSeedData();
  const db = await openDatabase();
  const transaction = db.transaction(
    [STORE_PROFILES, STORE_YEARS, STORE_BUDGET_CATEGORIES, STORE_EXPENSES, STORE_APP_META],
    "readwrite"
  );
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const budgetCategoriesStore = transaction.objectStore(STORE_BUDGET_CATEGORIES);
  const expensesStore = transaction.objectStore(STORE_EXPENSES);
  const appMetaStore = transaction.objectStore(STORE_APP_META);
  const [profile, targetYearRecord, categories, expenses, activeProfileIdRecord, activeYearRecord] = await Promise.all([
    requestToPromise(profilesStore.get(profileId)),
    requestToPromise(yearsStore.index("profileYear").get([profileId, year])),
    readAllFromIndex(budgetCategoriesStore, "profileYear", [profileId, year]),
    readAllFromIndex(expensesStore, "profileYear", [profileId, year]),
    requestToPromise(appMetaStore.get("activeProfileId")),
    requestToPromise(appMetaStore.get("activeYear"))
  ]);

  if (!profile || profile.archivedAt) {
    transaction.abort();
    throw new Error(`Profile "${profileId}" could not be found.`);
  }

  if (!targetYearRecord) {
    transaction.abort();
    throw new Error(`Year "${year}" could not be deleted.`);
  }

  yearsStore.delete(targetYearRecord.id);
  categories.forEach((category) => {
    budgetCategoriesStore.delete(category.id);
  });
  expenses.forEach((expense) => {
    expensesStore.delete(expense.id);
  });

  let nextScope = null;
  const activeProfileId = activeProfileIdRecord?.value ?? DEFAULT_PROFILE_ID;
  const activeYear = activeYearRecord?.value ?? CURRENT_YEAR;

  if (activeProfileId === profileId && activeYear === year) {
    const now = new Date().toISOString();
    const fallbackYearRecord = await ensureAvailableYearScope(yearsStore, profileId, now);
    appMetaStore.put({ key: "activeYear", value: fallbackYearRecord.year });
    nextScope = {
      profileId,
      year: fallbackYearRecord.year
    };
  }

  await transactionDone(transaction);
  emitProfileDataChanged({
    profileId,
    reason: "delete-year",
    changeType: "structure"
  });

  return {
    deletedYear: normalizeYearRecord(targetYearRecord),
    nextScope
  };
}

export async function renameProfile(profileId, nextName) {
  await ensureSeedData();
  const trimmedName = String(nextName ?? "").trim();
  if (!trimmedName) {
    throw new Error("Profile name is required.");
  }

  const db = await openDatabase();
  const transaction = db.transaction(STORE_PROFILES, "readwrite");
  const store = transaction.objectStore(STORE_PROFILES);
  const existing = await requestToPromise(store.get(profileId));

  if (!existing || existing.archivedAt) {
    transaction.abort();
    throw new Error(`Profile "${profileId}" could not be renamed.`);
  }

  store.put({
    ...existing,
    name: trimmedName,
    updatedAt: new Date().toISOString()
  });
  await transactionDone(transaction);
  emitProfileDataChanged({
    profileId,
    reason: "rename-profile",
    changeType: "structure"
  });

  return normalizeProfileRecord({
    ...existing,
    name: trimmedName
  });
}

export async function archiveProfile(profileId) {
  await ensureSeedData();
  const db = await openDatabase();
  const transaction = db.transaction(
    [
      STORE_PROFILES,
      STORE_PROFILE_SETTINGS,
      STORE_YEARS,
      STORE_BUDGET_CATEGORIES,
      STORE_EXPENSES,
      STORE_APP_META
    ],
    "readwrite"
  );
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const profileSettingsStore = transaction.objectStore(STORE_PROFILE_SETTINGS);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const budgetCategoriesStore = transaction.objectStore(STORE_BUDGET_CATEGORIES);
  const expensesStore = transaction.objectStore(STORE_EXPENSES);
  const appMetaStore = transaction.objectStore(STORE_APP_META);
  const [profiles, activeProfileRecord] = await Promise.all([
    readAllFromStore(profilesStore),
    requestToPromise(appMetaStore.get("activeProfileId"))
  ]);
  const activeProfileId = activeProfileRecord?.value ?? DEFAULT_PROFILE_ID;
  const targetProfile = profiles.find((profile) => profile.id === profileId) ?? null;

  if (!targetProfile || targetProfile.archivedAt) {
    transaction.abort();
    throw new Error(`Profile "${profileId}" could not be deleted.`);
  }

  const activeProfiles = profiles.filter((profile) => !profile.archivedAt);
  if (activeProfiles.length <= 1) {
    transaction.abort();
    throw new Error("The last remaining profile cannot be deleted.");
  }

  let nextScope = null;

  if (activeProfileId === profileId) {
    const fallbackProfile = activeProfiles
      .filter((profile) => profile.id !== profileId)
      .sort((left, right) => {
        if (left.isDefault !== right.isDefault) {
          return left.isDefault ? -1 : 1;
        }

        return left.name.localeCompare(right.name);
      })[0];

    if (!fallbackProfile) {
      transaction.abort();
      throw new Error("A replacement active profile could not be found.");
    }

    const fallbackYearRecord = await ensureAvailableYearScope(yearsStore, fallbackProfile.id, new Date().toISOString());
    const fallbackYear = String(fallbackYearRecord.year ?? "").trim();

    if (!fallbackYear) {
      transaction.abort();
      throw new Error(`Profile "${fallbackProfile.name}" has no available year.`);
    }

    appMetaStore.put({ key: "activeProfileId", value: fallbackProfile.id });
    appMetaStore.put({ key: "activeYear", value: fallbackYear });
    nextScope = {
      profileId: fallbackProfile.id,
      year: fallbackYear
    };
  }

  profilesStore.delete(profileId);
  profileSettingsStore.delete(profileId);

  const [profileYears, allCategories, allExpenses] = await Promise.all([
    readAllFromIndex(yearsStore, "profileId", profileId),
    readAllFromStore(budgetCategoriesStore),
    readAllFromStore(expensesStore)
  ]);
  const profileCategories = allCategories.filter((category) => category.profileId === profileId);
  const profileExpenses = allExpenses.filter((expense) => expense.profileId === profileId);

  profileYears.forEach((yearRecord) => {
    yearsStore.delete(yearRecord.id);
  });
  profileCategories.forEach((category) => {
    budgetCategoriesStore.delete(category.id);
  });
  profileExpenses.forEach((expense) => {
    expensesStore.delete(expense.id);
  });

  await transactionDone(transaction);

  return {
    deletedProfile: normalizeProfileRecord(targetProfile),
    nextScope
  };
}

export async function createProfile(name, options = {}) {
  await ensureSeedData();
  const trimmedName = String(name ?? "").trim();
  if (!trimmedName) {
    throw new Error("Profile name is required.");
  }

  const year = String(options.year ?? CURRENT_YEAR).trim();
  if (!year) {
    throw new Error("Profile year is required.");
  }

  const now = new Date().toISOString();
  const profileId = options.profileId ?? `profile-${crypto.randomUUID()}`;
  const yearId = `${profileId}:${year}`;
  const db = await openDatabase();
  const transaction = db.transaction([STORE_PROFILES, STORE_YEARS], "readwrite");
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const yearsStore = transaction.objectStore(STORE_YEARS);

  profilesStore.put({
    id: profileId,
    name: trimmedName,
    source: options.source ?? "local",
    isDefault: false,
    archivedAt: null,
    createdAt: now,
    updatedAt: now
  });
  yearsStore.put({
    id: yearId,
    profileId,
    year,
    archivedAt: null,
    createdAt: now,
    updatedAt: now
  });

  await transactionDone(transaction);

  if (options.activate) {
    await setActiveBudgetScope(profileId, year);
  }

  return {
    profile: normalizeProfileRecord({
      id: profileId,
      name: trimmedName,
      source: options.source ?? "local",
      isDefault: false,
      archivedAt: null,
      createdAt: now,
      updatedAt: now
    }),
    year: normalizeYearRecord({
      id: yearId,
      profileId,
      year,
      archivedAt: null,
      createdAt: now,
      updatedAt: now
    })
  };
}

export async function createProfileYear(profileId, year, options = {}) {
  await ensureSeedData();
  const trimmedYear = String(year ?? "").trim();
  if (!trimmedYear) {
    throw new Error("Profile year is required.");
  }

  const db = await openDatabase();
  const transaction = db.transaction([STORE_PROFILES, STORE_YEARS], "readwrite");
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const [profile, existingYearRecord] = await Promise.all([
    requestToPromise(profilesStore.get(profileId)),
    requestToPromise(yearsStore.index("profileYear").get([profileId, trimmedYear]))
  ]);

  if (!profile || profile.archivedAt) {
    transaction.abort();
    throw new Error(`Profile "${profileId}" could not be found.`);
  }

  if (existingYearRecord) {
    transaction.abort();
    throw new Error(`Year "${trimmedYear}" already exists for profile "${profileId}".`);
  }

  const now = new Date().toISOString();
  const yearRecord = {
    id: `${profileId}:${trimmedYear}`,
    profileId,
    year: trimmedYear,
    archivedAt: null,
    createdAt: now,
    updatedAt: now
  };
  yearsStore.put(yearRecord);
  await transactionDone(transaction);

  const scope = options.activate === false
    ? { profileId, year: trimmedYear }
    : await setActiveBudgetScope(profileId, trimmedYear);

  return {
    scope,
    year: normalizeYearRecord(yearRecord)
  };
}

export async function getProfileSettings(profileId) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_PROFILE_SETTINGS, "readonly");
  const record = await requestToPromise(transaction.objectStore(STORE_PROFILE_SETTINGS).get(profileId));
  await transactionDone(transaction);

  return normalizeProfileSettings(record?.settings);
}

export async function updateProfileSettings(profileId, nextSettings) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_PROFILE_SETTINGS, "readwrite");
  const store = transaction.objectStore(STORE_PROFILE_SETTINGS);
  const existingRecord = await requestToPromise(store.get(profileId));
  const now = new Date().toISOString();
  const settings = mergeProfileSettings(existingRecord?.settings, nextSettings);

  store.put({
    profileId,
    settings,
    createdAt: existingRecord?.createdAt ?? now,
    updatedAt: now
  });

  await transactionDone(transaction);
  emitProfileDataChanged({
    profileId,
    reason: "update-profile-settings",
    changeType: "preferences"
  });

  return settings;
}
